import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Avatar, Box, Button, Chip, Grid, Paper, Stack, Tab, Tabs, Typography, Skeleton, alpha, useTheme,
} from '@mui/material';
import { ArrowBack, Info, Payments, Timeline, Star } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import AmountBreakdown from '../../components/ui/AmountBreakdown';
import GoogleMapPanel from '../../components/maps/GoogleMapPanel';
import { getTripGodView } from '../../services/opsViewService';
import { formatMoney } from '../../utils/currency';

const TEAL = '#0d9488';
const TABS = [
  { key: 'overview', label: 'Overview', icon: <Info fontSize="small" /> },
  { key: 'fare', label: 'Fare', icon: <Payments fontSize="small" /> },
  { key: 'timeline', label: 'Timeline', icon: <Timeline fontSize="small" /> },
  { key: 'rating', label: 'Rating', icon: <Star fontSize="small" /> },
] as const;

export default function TripDetailPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { tripId = '' } = useParams<{ tripId: string }>();
  const location = useLocation();
  const backTo = (location.state as { from?: string } | null)?.from || '/reports';
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('overview');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['trip-god-view', tripId],
    queryFn: () => getTripGodView(tripId),
    enabled: !!tripId,
  });

  const trip = data?.trip || {};
  const title = String(trip.tripId || 'Trip');
  const fare = (trip.fare || {}) as Record<string, unknown>;

  const mapMarkers = useMemo(() => {
    const markers: { lat: number; lng: number; title?: string; color?: string }[] = [];
    const pickupLat = trip.pickupLat as number | undefined;
    const pickupLng = trip.pickupLng as number | undefined;
    const dropLat = trip.dropLat as number | undefined;
    const dropLng = trip.dropLng as number | undefined;
    if (typeof pickupLat === 'number' && typeof pickupLng === 'number') {
      markers.push({ lat: pickupLat, lng: pickupLng, title: 'Pickup', color: '#16a34a' });
    }
    if (typeof dropLat === 'number' && typeof dropLng === 'number') {
      markers.push({ lat: dropLat, lng: dropLng, title: 'Drop', color: '#dc2626' });
    }
    return markers;
  }, [trip.pickupLat, trip.pickupLng, trip.dropLat, trip.dropLng]);

  const mapRoute = useMemo(
    () => mapMarkers.map((m) => ({ lat: m.lat, lng: m.lng })),
    [mapMarkers],
  );

  const fareRows = Object.entries(fare)
    .filter(([, v]) => typeof v === 'number')
    .map(([k, v]) => ({
      _id: k,
      label: k.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()),
      value: formatMoney(v, trip),
    }));
  const cancelReason = String(trip.cancelReason || trip.cancellationReason || '').trim();
  const cancelledBy = String(trip.cancelledBy || '').trim();

  const timelineRows = (data?.timeline || []).map((e) => ({
    _id: e.key,
    label: e.label,
    at: e.at ? new Date(e.at).toLocaleString() : '—',
  }));

  const ratingCols: GridColDef[] = [
    { field: 'reviewer', headerName: 'Reviewer', flex: 1, minWidth: 140 },
    { field: 'rating', headerName: 'Rating', width: 90 },
    { field: 'review', headerName: 'Review', flex: 1.4, minWidth: 180 },
    { field: 'createdAt', headerName: 'Date', width: 160, valueGetter: (_v, row) => row.createdAt ? new Date(row.createdAt as string).toLocaleString() : '-' },
  ];

  return (
    <PageShell maxWidth={false}>
      <PageHeader
        eyebrow="Trips"
        title={title}
        subtitle="Trip detail — fare, timeline, customer, and partner"
        breadcrumbs={[{ label: 'Reports', href: '/reports' }, { label: title }]}
        actions={
          <Button startIcon={<ArrowBack />} variant="outlined" onClick={() => navigate(backTo)}>
            Back
          </Button>
        }
      />

      {isError && (
        <Paper sx={{ p: 3, mb: 2, borderRadius: 2 }}>
          <Typography color="error">Could not load this trip.</Typography>
        </Paper>
      )}

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={3} lg={3}>
          <Paper elevation={0} sx={{ p: 2.5, height: '100%', borderRadius: 2.5, border: 1, borderColor: 'divider' }}>
            {isLoading ? (
              <Stack alignItems="center" spacing={1}>
                <Skeleton variant="circular" width={84} height={84} />
                <Skeleton width={140} />
              </Stack>
            ) : (
              <>
                <Stack alignItems="center" spacing={1}>
                  <Avatar sx={{ width: 84, height: 84, bgcolor: 'grey.200', fontSize: 32, color: 'text.secondary' }}>T</Avatar>
                  <Typography fontWeight={800}>{title}</Typography>
                  <Chip size="small" label={String(trip.status || '').replace(/_/g, ' ')} />
                </Stack>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', my: 2 }} />
                <Stack spacing={1.25}>
                  {[
                    ['Trip ID', String(trip.tripId || '—')],
                    ['Status', String(trip.status || '').replace(/_/g, ' ') || '—'],
                    ['Trip type', String(trip.tripType || '—')],
                    ['Payment', String(trip.paymentMethod || '—')],
                    ['Total', `${formatMoney((fare.total as number) ?? 0, trip)}`],
                    ['Zone', String(trip.zone || '—')],
                    ...(cancelReason ? [['Cancel reason', cancelReason]] : []),
                    ...(cancelledBy ? [['Cancelled by', cancelledBy.replace(/_/g, ' ')]] : []),
                  ].map(([k, v]) => (
                    <Stack key={k} direction="row" justifyContent="space-between" spacing={1}>
                      <Typography variant="body2" color="text.secondary">{k}</Typography>
                      <Typography variant="body2" fontWeight={700}>{v}</Typography>
                    </Stack>
                  ))}
                </Stack>
                {data?.customer && (
                  <Button
                    fullWidth sx={{ mt: 2 }} variant="outlined"
                    onClick={() => navigate(`/customers/${data.customer!._id}`, { state: { from: `/trips/${tripId}` } })}
                  >
                    Customer: {data.customer.name}
                  </Button>
                )}
                {data?.driver && (
                  <Button
                    fullWidth sx={{ mt: 1 }} variant="outlined"
                    onClick={() => navigate(`/partners/${data.driver!._id}`, { state: { from: `/trips/${tripId}` } })}
                  >
                    Partner: {data.driver.name}
                  </Button>
                )}
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={9} lg={9} sx={{ minWidth: 0 }}>
          <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ px: 1, pt: 1, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                allowScrollButtonsMobile
                TabIndicatorProps={{ sx: { display: 'none' } }}
              >
                {TABS.map((t) => (
                  <Tab
                    key={t.key}
                    value={t.key}
                    icon={t.icon}
                    iconPosition="start"
                    label={t.label}
                    sx={{
                      minHeight: 42, textTransform: 'none', fontWeight: 700, borderRadius: 1, mx: 0.25,
                      color: 'text.secondary',
                      '&.Mui-selected': { bgcolor: TEAL, color: '#fff' },
                      '& .MuiTab-iconWrapper': { color: 'inherit' },
                    }}
                  />
                ))}
              </Tabs>
            </Box>
            <Box sx={{ p: 2 }}>
              {isLoading ? <Skeleton height={280} /> : tab === 'overview' ? (
                <Stack spacing={2}>
                  <AmountBreakdown
                    fare={fare}
                    total={Number(fare.total ?? 0)}
                    paymentMethod={trip.paymentMethod ? String(trip.paymentMethod) : undefined}
                    paymentStatus={trip.paymentStatus ? String(trip.paymentStatus) : undefined}
                    currency={trip}
                  />
                  {mapMarkers.length > 0 && (
                    <GoogleMapPanel height={280} markers={mapMarkers} route={mapRoute} />
                  )}
                  {(data?.fields || []).map(([k, v]) => (
                    <Stack key={k} direction="row" justifyContent="space-between" sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">{k}</Typography>
                      <Typography variant="body2" fontWeight={700}>{v || '—'}</Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : tab === 'fare' ? (
                <EnhancedDataGrid
                  rows={fareRows}
                  columns={[
                    { field: 'label', headerName: 'Component', flex: 1, minWidth: 160 },
                    { field: 'value', headerName: 'Amount', width: 160 },
                  ]}
                  getRowId={(r) => String(r._id)}
                  autoHeight
                  disableLocalSearch
                  exportFileName={`${title}-fare`}
                />
              ) : tab === 'timeline' ? (
                <EnhancedDataGrid
                  rows={timelineRows}
                  columns={[
                    { field: 'label', headerName: 'Event', flex: 1, minWidth: 160 },
                    { field: 'at', headerName: 'Time', width: 220 },
                  ]}
                  getRowId={(r) => String(r._id)}
                  autoHeight
                  disableLocalSearch
                  exportFileName={`${title}-timeline`}
                />
              ) : (
                <EnhancedDataGrid
                  rows={data?.ratings || []}
                  columns={ratingCols}
                  getRowId={(r) => String(r._id)}
                  autoHeight
                  disableLocalSearch
                  exportFileName={`${title}-ratings`}
                />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </PageShell>
  );
}
